// libs
import { styled } from '@nykaa/ui-components';
import { css } from '@emotion/core';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';

// styles
import { BREAKPOINTS, FOCUS_VISIBILITY_CSS } from 'shared/styles/base';
import { BaseButton, truncate } from 'shared/styles/mixin';

//icons
import ChevronDownSmall from '@nykaa/ui-components/Icons/chevron-down-small';

//components
import CircleLock from '@nykaa/ui-components/Icons/circle-lock';
import CouponsExpiry from 'shared/layouts/Coupons/CouponsExpiry';
import GradientText from 'shared/components/GradientText';

// styles
export const Container = styled.div<{
  scrollSnap: boolean;
  couponDisabled: boolean;
  isExpired: boolean;
}>`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.surface};
  @media (max-width: ${BREAKPOINTS.DESKTOP}px) {
    :first-child {
      .coupon-expiry {
        border-radius: ${({ theme }) => `${theme.borders.radius10} 0 `};
        left: 16px;
      }
    }
  }
  .coupon-expiry {
    border-radius: ${({ theme }) => `${theme.borders.radius10} 0 `};
  }

  ${({ isExpired, theme }) =>
    isExpired &&
    `
    .infoContainer, .action-container{

      svg{
        opacity: 44%;
      }
    }
    .action-container>button, .couponCode, .couponTitle, .couponDescription>p, .couponDescription>button>span{
      color: ${theme.colors.textDisabled}
    }
    .action-overlay{
      background: transparent;
      height: 40px;
      width: 150px;
      position: absolute;
      bottom: 0;
      right: 18px;
    }
  `};
  ${({ theme, couponDisabled }) => !couponDisabled && `padding-top: ${theme.spacing.spacing50};`}
  ${({ scrollSnap, theme }) =>
    scrollSnap &&
    `margin: 0px ${theme.spacing.spacing40} 0px 0px;
    flex: 0 0 auto;
    scroll-snap-align: start;
    width: calc(90% - 10px);
    @media (max-width: ${BREAKPOINTS.DESKTOP}px) {
      margin: 0px ${theme.spacing.spacing50} 0px 0px;
    :first-child{
      padding-left: ${theme.spacing.spacing80};
      box-sizing: content-box;
    }
    :last-child{
      padding-right: ${theme.spacing.spacing80};
      box-sizing: content-box;
    }
  }`};
`;

export const SaveText = styled.div<{ isApplied: boolean; }>`
  ${({ theme }) => theme.typography.subTitleSmall}
  background-color: ${({ theme }) => theme.colors.positive10};
  padding: ${({ theme }) => `0 ${theme.spacing.spacing40} 0 ${theme.spacing.spacing40}`};
  border: 1px solid ${({ theme }) => hexToRgb(theme.colors.positive, 0.16)};
  position: absolute;
  top: 0px;
  right: 16px;
  color: ${({ theme }) => theme.colors.positive};
  display: flex;
  align-items: center;
  border-radius: ${({ theme }) => theme.borders.radius40};
  margin: ${({ theme }) => theme.spacing.spacing0};
  z-index: 1;
  .appliedTick {
    height: 14px;
    width: 14px;
    margin: ${({ theme }) =>
    `${theme.spacing.spacing10} ${theme.spacing.spacing20} ${theme.spacing.spacing10} 0`};
  }
  ${({ theme, isApplied }) => isApplied && `padding-left: ${theme.spacing.spacing20}`};
  .saveText {
    margin: ${({ theme }) => `${theme.spacing.spacing10} 0`};
  }
`;

export const InfoContainer = styled.div<{
  disabled: boolean;
  backgroundImage: string;
  hideBottomBorder: boolean;
  isOffer: boolean;
  isBottomSheet?: boolean;
}>`
  ${({ theme, disabled, backgroundImage }) => css`
    border: 1px solid ${theme.colors.textOutline};
    border-radius: ${theme.borders.radius10} ${theme.borders.radius10} ${theme.borders.radiusNone}
      ${theme.borders.radiusNone};
    padding: ${theme.spacing.spacing50} ${theme.spacing.spacing80} ${theme.spacing.spacing60}
      ${theme.spacing.spacing80};
    ${disabled && `padding-top: ${theme.spacing.spacing60};`};
    background-image: url(${backgroundImage});
  `};
  ${({ hideBottomBorder }) => hideBottomBorder && `border-bottom: none;`};
  width: 100%;
  background-repeat: no-repeat;
  background-size: cover;
  position:${({ isBottomSheet }) => (isBottomSheet ? '' : 'relative')};
  height: ${({ isOffer }) => (!isOffer ? '112px' : '84px')};

  ${FOCUS_VISIBILITY_CSS}
`;

export const InfoContent = styled.div<{
  disabled: boolean;
}>`
  display: flex;
  max-width: 100%;
  margin-top: ${({ theme, disabled }) => disabled === false && theme.spacing.spacing40};
  max-width: 100%;
  align-items: center;
`;

export const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.spacing80};
  overflow: hidden;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  overflow: hidden;
`;

export const Title = styled.h3`
  ${truncate};
  margin: 0;
  ${({ theme }) =>
    css`
      ${theme.typography.buttonMedium};
      color: ${theme.colors.textPrimary};
    `};
`;

export const Description = styled.div`
  position: relative;
  height: auto;
  overflow: hidden;
  ${({ theme }) => theme.typography.bodySmall}
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.spacing10};
  p {
    display: inline;
    margin: ${({ theme }) => `${theme.spacing.spacing10} 0 0 0`};
    height: 40px;
  }
`;

export const DetailsButton = styled(BaseButton)`
  display: inline-flex;
  align-items: center;
  ${FOCUS_VISIBILITY_CSS}
`;

export const DetailText = styled.span`
  ${({ theme }) => theme.typography.buttonSmall}
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const DownwardChevron = styled(ChevronDownSmall)`
  height: 16px;
  width: 16px;
  margin-left: ${({ theme }) => theme.spacing.spacing20};
`;

export const ActionContainer = styled.div<{ isApplied: boolean; }>`
  display: flex;
  justify-content: space-between;
  border-radius: ${({ theme }) => theme.borders.radius10};
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='2' ry='2' stroke='%23d6d9dc' stroke-width='2' stroke-dasharray='3%2c 3' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
  border-radius: ${({ theme }) => `0 0 ${theme.borders.radius10} ${theme.borders.radius10}`};
  width: 100%;
  ${({ isApplied, theme }) => isApplied && `background-color: ${theme.colors.positive10};`}
`;

export const CouponCodeContainer = styled.div<{ isViewProduct: boolean; }>`
  display: flex;
  align-items: center;
  // 95 is button size in case of view product 140 is button size
  width: ${({ isViewProduct }) => (isViewProduct ? `calc(100% - 140px);` : `calc(100% - 95px)`)};
`;

export const CopyContainer = styled.span`
  cursor: pointer;
  margin-top: ${({ theme }) => theme.spacing.spacing20};

  ${FOCUS_VISIBILITY_CSS}
`;

export const CouponCode = styled.div`
  ${truncate};
  ${({ theme }) => theme.typography.subTitleMedium}
  ${({ theme }) => `
    margin: ${`${theme.spacing.spacing50} ${theme.spacing.spacing20} ${theme.spacing.spacing50} ${theme.spacing.spacing80}`};
    color: ${theme.colors.textSecondary};
  `}
  letter-spacing: 0.3px;
`;

export const AppliedText = styled.div<{ disabled: boolean; }>`
  ${({ theme }) => theme.typography.labelSmall}
  ${({ theme }) => `
    padding: ${`${theme.spacing.spacing20} ${theme.spacing.spacing40}`};
    background-color: ${theme.colors.positive};
    color: ${theme.colors.textInversePrimary};
    margin: ${`0 ${theme.spacing.spacing20}`};
    border-radius: ${theme.borders.radius10};
  `}
`;

export const Button = styled.button<{ disabled: boolean; isApplied: boolean; }>`
  border: 0;
  ${({ theme }) => theme.typography.buttonMedium}
  align-items: center;
  color: ${({ theme }) => theme.colors.secondary};
  padding: ${({ theme }) => `${theme.spacing.spacing50} ${theme.spacing.spacing100}`};
  background: transparent;
  outline: none;
  cursor: pointer;
  ${({ disabled, theme }) => disabled && `color: ${theme.colors.textDisabled};`}
  ${({ isApplied, theme }) => isApplied && `color: ${theme.colors.textSecondary}; `}

  ${FOCUS_VISIBILITY_CSS}
`;

export const IconContainerWrapper = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
`;

export const LockIcon = styled(CircleLock)`
  border-radius: 50%;
  position: absolute;
  bottom: 8px;
  background-color: ${({ theme }) => theme.colors.secondary10};
  right: 66px;
`;

export const CouponsExpiryTimer = styled(CouponsExpiry)`
  position: absolute;
  left: 0;
  ${({ theme }) => theme.typography.bodyXSmall}
  .coupon-timer {
    ${({ theme }) => theme.typography.labelSmall}
  }
`;

export const ExpiredText = styled.div`
  ${({ theme }) => theme.typography.bodyXSmall};
  position: absolute;
  left: 0;
  background: rgba(253, 118, 133, 0.08);
  width: fit-content;
  ${({ theme }) => ` padding: ${theme.spacing.spacing10} ${theme.spacing.spacing40};`};
`;

export const SaleExpWrapper = styled.div<{ bg: string; }>`
  background-color: ${({ bg }) => bg};
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => ` ${theme.spacing.spacing10} ${theme.spacing.spacing20}`};
  ${({ theme }) => theme.typography.bodyXSmall};
  gap: ${({ theme }) => theme.spacing.spacing20};
  color: ${({ theme }) => theme.colors.textPrimary};
`;
export const SaleExpText = styled(GradientText) <{ bold?: boolean; }>`
  ${({ theme, bold }) => bold ? theme.typography.labelSmall : theme.typography.bodyXSmall};
`;